<template>
  <div class="sign-lawsuit-wrapper">
    <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
    <div class="sign-lawsuit-container">
      <iframe
        v-if="documentViewerUrl"
        :src="documentViewerUrl"
        class="lawsuit-document"
        title="lawsuit-document"
      />
      <div class="sign-block">
        <div class="sign-title">Подпись:</div>
        <div class="sign-offer-item" @click.stop="checkboxHandler()">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isLawsuitAgreed }"
          >
            <img
              v-if="isLawsuitAgreed"
              class="check-box-image"
              src="../../../public/custom/images/check.svg"
              alt="Checked image"
            />
          </div>
          <div class="check-box-label">
            Текст искового заявления прочитан мной полностью, с условиями ознакомлен(а) и согласен(а)
          </div>
        </div>
        <div class="lawsuit-upload-block">
          <sign-up-documents-uploader
            v-model="arbitrationPaymentFiles"
            title="Загрузка чека оплаты третейского суда *"
            hint="Прикрепите чек оплаты третейского суда в формате PDF. Максимум 1 файл."
            attach-btn="Прикрепить"
            :max-files="1"
            accepted-file-types=".pdf"
            :file-pattern="lawsuitBillFilePattern"
          />
        </div>
        <div class="lawsuit-upload-block">
          <sign-up-documents-uploader
            v-model="borrowerClaimResponseFiles"
            title="Загрузка ответа заемщика на претензию"
            hint="Если есть ответ заемщика на претензию, прикрепите его. Максимум 1 файл."
            attach-btn="Прикрепить"
            :max-files="1"
            accepted-file-types=".jpeg,.jpg,.png,.pdf,.tiff,.doc,.docx"
            :file-pattern="lawsuitResponseFilePattern"
          />
        </div>
        <div class="otp-container">
          <div class="otp-block">
            <base-text-input
              :mask-pattern="'######'"
              :name="'lawsuitOtp'"
              :validate-rules="'otp'"
              :placeholder="$t('message.contract.otp.placeholder')"
              v-model="otp"
              :disabled="!isLawsuitAgreed"
              :is-has-error-block="false"
            />
          </div>
          <div class="send-otp-btn">
            <base-button
              text="Получить код"
              :disabled="!isLawsuitAgreed"
              @click="getOtpCode()"
              min-height="40px"
            />
          </div>
        </div>
        <div class="lender-block">
          <div class="lender-title">Займодавец:</div>
          <div class="lender-full-name">{{ lenderFullName }}</div>
        </div>
        <div class="sign-lawsuit-btn">
          <base-button
            text="Подписать исковое заявление"
            :disabled="isSignLawsuitBtnDisabled"
            :is-loading="isSignLawsuitBtnLoading"
            @click="signLawsuit()"
            min-height="40px"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseTextInput from '../../components/base/BaseTextInput.vue';
import BaseButton from '../../components/base/BaseButton.vue';
import SignUpDocumentsUploader from '../../components/common/SignUpDocumentsUploader.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'SignLawsuit',
  inject: ['$httpService'],
  components: {
    BaseTextInput,
    BaseButton,
    SignUpDocumentsUploader
  },
  data() {
    return {
      blobUrl: '',
      arbitrationPaymentFiles: [],
      borrowerClaimResponseFiles: [],
      lawsuitBillFilePattern: /\.pdf$/i,
      lawsuitResponseFilePattern: /\.(jpeg|jpg|png|pdf|tiff|doc|docx)$/i,
      isLawsuitAgreed: false,
      isOtpRequested: false,
      isSignLawsuitBtnLoading: false,
      otp: ''
    };
  },
  computed: {
    ...mapGetters(['profile']),
    documentViewerUrl() {
      if (!this.blobUrl) {
        return '';
      }

      return `${this.blobUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&zoom=100`;
    },
    lenderFullName() {
      const lastName = this.profile?.name_ru?.family_name_ru || '';
      const firstName = this.profile?.name_ru?.given_name_ru || '';
      const middleName = this.profile?.name_ru?.middle_name_ru || '';

      return [lastName, firstName, middleName].filter(Boolean).join(' ');
    },
    isSignLawsuitBtnDisabled() {
      return !this.isLawsuitAgreed
        || !this.isOtpRequested
        || !this.otp
        || this.errors.has('lawsuitOtp')
        || !this.arbitrationPaymentFiles.length
        || this.isSignLawsuitBtnLoading;
    }
  },
  async created() {
    const publicId = this.$route.params.id;

    this.$store.dispatch('loading', true);

    try {
      const response = await this.$httpService.claim.downloadLawsuite(publicId);
      this.showDocument(response);
    } catch (error) {
      console.log(error);
    } finally {
      this.$store.dispatch('loading', false);
    }
  },
  beforeDestroy() {
    this.revokeDocumentUrl();
  },
  methods: {
    checkboxHandler() {
      this.isLawsuitAgreed = !this.isLawsuitAgreed;

      if (!this.isLawsuitAgreed) {
        this.otp = '';
        this.isOtpRequested = false;
      }
    },
    async getOtpCode() {
      if (!this.isLawsuitAgreed) {
        return;
      }

      try {
        await this.$httpService.loan.getOtpCode();
        this.isOtpRequested = true;
        this.$toast.success(this.$t('message.signIn.otpToastMsg'));
      } catch (error) {
        console.log(error);
      }
    },
    buildSignLawsuitFormData() {
      const formData = new FormData();

      formData.append('lawsuit_public_id', this.$route.params.id);
      formData.append('otp', String(this.otp));

      this.arbitrationPaymentFiles.forEach((item) => {
        formData.append('bill', item.file);
      });

      this.borrowerClaimResponseFiles.forEach((item) => {
        formData.append('response', item.file);
      });

      return formData;
    },
    async signLawsuit() {
      if (this.isSignLawsuitBtnDisabled) {
        return;
      }

      this.isSignLawsuitBtnLoading = true;

      try {
        await this.$httpService.claim.signLawsuite(this.buildSignLawsuitFormData());
        this.$toast.success('Исковое заявление успешно подписано');
        this.$router.push({
          name: 'profile',
          params: this.$route.params.backRouteParams || {}
        });
      } catch (error) {
        this.$toast.error('Не удалось подписать исковое заявление');
      } finally {
        this.isSignLawsuitBtnLoading = false;
      }
    },
    showDocument(response) {
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });

      this.revokeDocumentUrl();
      this.blobUrl = window.URL.createObjectURL(blob);
    },
    revokeDocumentUrl() {
      if (this.blobUrl) {
        window.URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = '';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  .sign-lawsuit-wrapper {
    @include page-wrapper-with-header-and-footer;

    padding-top: 40px;
    box-sizing: border-box;
  }

  .sign-lawsuit-container {
    @include base-page-container;

    flex-direction: column;
    min-height: calc(100vh - 434px);
  }

  .route-name {
    @include route-name;
    width: 95%;
    text-align: start;
    margin-bottom: 20px;
    margin-top: -20px;
  }

  .lawsuit-document {
    width: 100%;
    flex: 1;
    min-height: 700px;
    border: none;
    border-radius: 10px;
    background-color: #ffffff;
  }

  .sign-block {
    width: 100%;
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;
  }

  .sign-title {
    margin-bottom: 20px;
    font-weight: 700;
    font-size: 16px;
    line-height: 135%;
    color: #121212;
  }

  .sign-offer-item {
    margin-bottom: 30px;
    display: flex;
    align-items: flex-start;
    cursor: pointer;
  }

  .check-box-btn {
    @include base-check-box;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  .check-box-image {
    width: 12px;
  }

  .check-box-label {
    @include base-check-box-label;

    font-size: 16px;
    line-height: 135%;
    font-weight: 500;
  }

  .lawsuit-upload-block {
    width: 100%;
    margin-bottom: 20px;
  }

  .otp-container {
    display: flex;
    user-select: none;
  }

  .otp-block {
    width: 280px;
    margin-right: 20px;
  }

  .send-otp-btn {
    width: 160px;
    height: 40px;
  }

  .lender-block {
    width: 100%;
    margin-top: 30px;
    display: flex;
    flex-direction: column;
  }

  .lender-title {
    margin-bottom: 10px;
    font-weight: 600;
    font-size: 16px;
    line-height: 135%;
    color: #121212;
  }

  .lender-full-name {
    font-weight: 400;
    font-size: 16px;
    line-height: 135%;
    color: #121212;
    text-transform: uppercase;
  }

  .sign-lawsuit-btn {
    width: 260px;
    min-width: 260px;
    max-width: 260px;
    height: 40px;
    margin-top: 30px;
    flex-shrink: 0;
  }

@media screen and (max-width: 620px) {
  .otp-container {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .otp-block {
    width: 100%;
    margin-right: 0;
  }

  .send-otp-btn {
    width: 100%;
  }
}
</style>
